import React, { useEffect, useState, useMemo } from 'react';
import {
  Modal,
  Box,
  Paper,
  Typography,
  IconButton,
  List,
  ListItemButton,
  ListItemText,
  Button,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import toast from 'react-hot-toast';
import axiosInstant from '../../../api';
import { videoShortlistsEndpoint, addVideoToVideoShortlist } from '../../../api/endpoints';
import { DotLottleLoader } from '../../../components/Loader/DotLottleLoader';
import IconSearch from '../../../assets/svg/search-icon-grey.svg';

interface Props {
  open: boolean;
  onClose: () => void;
  s3FileId: number;
}

export default function AddToVideoShortlistMenu({ open, onClose, s3FileId }: Props) {
  const [shortlists, setShortlists] = useState<any[]>([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [newName, setNewName] = useState('');
  const [creating, setCreating] = useState(false);

  const fetchShortlists = async () => {
    setLoading(true);
    try {
      const response = await axiosInstant.get(videoShortlistsEndpoint, {
        params: { all: 'true', s3_file_id: s3FileId },
      });
      setShortlists(response.data.rows || []);
    } catch (error) {
      console.error('Error fetching video shortlists:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchShortlists();
    setSearch('');
    setNewName('');
  }, [open]);

  const filtered = useMemo(() => {
    if (!search.trim()) return shortlists;
    return shortlists.filter((sl) =>
      sl.name.toLowerCase().includes(search.toLowerCase())
    );
  }, [shortlists, search]);

  const handleAdd = async (shortlistId: number, shortlistName: string) => {
    try {
      await axiosInstant.post(addVideoToVideoShortlist(shortlistId), { s3FileId });
      toast.success(`Added to "${shortlistName}"`);
      onClose();
    } catch (error: any) {
      const msg = error?.response?.data?.error || 'Failed to add video to shortlist';
      toast.error(msg);
    }
  };

  const handleCreate = async () => {
    if (!newName.trim()) return;
    setCreating(true);
    try {
      const response = await axiosInstant.post(videoShortlistsEndpoint, { name: newName.trim() });
      toast.success(`"${response.data.name}" created`);
      setNewName('');
      fetchShortlists();
    } catch (error: any) {
      const backendError = error?.response?.data?.error || error?.response?.data;
      if (backendError === 'NameExists') {
        toast.error('A video shortlist with this name already exists');
      } else {
        toast.error('Failed to create video shortlist');
      }
    } finally {
      setCreating(false);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box
        component={Paper}
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 700,
          height: '55vh',
          bgcolor: 'var(--surface)',
          borderRadius: '12px',
          boxShadow: 24,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: 3,
            pt: 2.5,
            pb: 1.5,
          }}
        >
          <Typography sx={{ fontSize: '24px', fontWeight: 900 }}>
            Video Shortlists
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{ display: 'flex', flex: 1, overflow: 'hidden', px: 3, pb: 3, gap: 2.5 }}>

          <Box
            sx={{
              width: 240,
              flexShrink: 0,
              borderRadius: '12px',
              border: '1px solid #D4C4A8',
              display: 'flex',
              flexDirection: 'column',
              overflow: 'hidden',
            }}
          >
            <Box
              sx={{
                background: 'linear-gradient(135deg, #AB854A 0%, #C9A96E 100%)',
                px: 2,
                py: 1.5,
              }}
            >
              <Typography
                sx={{
                  color: '#fff',
                  fontFamily: 'CircularStd',
                  fontSize: 14,
                  fontWeight: 700,
                }}
              >
                New Video Shortlist
              </Typography>
            </Box>

            <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, flex: 1 }}>
              <Typography
                sx={{
                  fontFamily: 'CircularStd',
                  fontSize: 13,
                  fontWeight: 500,
                  color: 'var(--text)',
                }}
              >
                Name
              </Typography>
              <TextField
                variant="outlined"
                fullWidth
                size="small"
                placeholder="Shortlist Name"
                value={newName}
                onChange={(e) => setNewName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleCreate(); }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: '10px',
                    backgroundColor: 'var(--surface-2)',
                    '& fieldset': { border: 'none' },
                  },
                  '& .MuiInputBase-input': {
                    fontFamily: 'CircularStd',
                    fontSize: 13,
                    padding: '10px 14px',
                  },
                }}
              />

              <Box sx={{ mt: 'auto', pt: 1 }}>
                <Button
                  onClick={handleCreate}
                  fullWidth
                  disabled={creating || !newName.trim()}
                  sx={{
                    backgroundColor: 'var(--accent)',
                    color: '#fff',
                    fontFamily: 'CircularStd',
                    fontSize: 13,
                    fontWeight: 700,
                    textTransform: 'none',
                    borderRadius: '10px',
                    py: 1,
                    '&:hover': { backgroundColor: '#8A6D41' },
                    '&:disabled': { backgroundColor: '#D4C4A8', color: '#fff' },
                  }}
                >
                  {creating ? 'Creating...' : 'Create'}
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ pb: 1.5 }}>
              <div className="w-full relative">
                <img
                  className="absolute top-1/2 transform -translate-x-1/2 -translate-y-1/2 left-5"
                  src={IconSearch}
                  alt="search"
                />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="w-full h-[36px] px-[2.25rem] py-0 bg-surface-2 text-text-2 placeholder-muted rounded-xl focus:outline-none focus:text-text-1 text-sm"
                  placeholder="Search"
                />
              </div>
            </Box>

            <Box sx={{ overflow: 'auto', flex: 1 }}>
              {loading ? (
                <Box className="flex justify-center py-6">
                  <DotLottleLoader fill={false} sx={{ width: 40, height: 40 }} />
                </Box>
              ) : filtered.length === 0 ? (
                <Typography variant="body2" color="textSecondary" sx={{ px: 2, py: 3, textAlign: 'center' }}>
                  {shortlists.length === 0 ? 'No video shortlists yet' : 'No matching shortlists'}
                </Typography>
              ) : (
                <List disablePadding>
                  {filtered.map((sl) => {
                    const added = !!sl.contains_video;
                    return (
                      <ListItemButton
                        key={sl.id}
                        disabled={added}
                        onClick={() => handleAdd(sl.id, sl.name)}
                        sx={{
                          borderRadius: '8px',
                          '&:hover': { backgroundColor: 'var(--surface-2)' },
                          '&.Mui-disabled': { opacity: 1, color: 'var(--muted)' },
                        }}
                      >
                        <ListItemText
                          primary={sl.name}
                          primaryTypographyProps={{
                            fontFamily: 'CircularStd',
                            fontSize: 14,
                            fontWeight: 500,
                            noWrap: true,
                            color: added ? 'var(--muted)' : undefined,
                          }}
                        />
                        {added && (
                          <span
                            className="shrink-0 uppercase font-bold text-muted"
                            style={{ fontSize: 10, letterSpacing: '.04em' }}
                          >
                            Added
                          </span>
                        )}
                      </ListItemButton>
                    );
                  })}
                </List>
              )}
            </Box>
          </Box>
        </Box>
      </Box>
    </Modal>
  );
}
